import React from 'react';

const STORAGE_KEY = 'expenseNavOrder';

function readFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

let orderedKeys: string[] = readFromStorage();
const listeners = new Set<() => void>();

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((key, i) => key === b[i]);
}

/**
 * Persist the ordered list of expense keys currently shown in the transaction
 * table so the expense detail page can move to the next/previous expense in the
 * exact order the user is looking at. Persisted to localStorage so the order
 * survives navigating into the detail route (and a refresh).
 */
export function setExpenseNavOrder(keys: string[]): void {
  if (sameOrder(keys, orderedKeys)) {
    return;
  }
  orderedKeys = keys;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Ignore storage errors (e.g. private mode); nav simply won't persist.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ordered expense keys matching the visible transaction list order. */
export function useExpenseNavOrder(): string[] {
  return React.useSyncExternalStore(subscribe, () => orderedKeys);
}
